import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ToggleSwitch from '@/components/ui/ToggleSwitch/ToggleSwitch';
import SuccessScreen from '@/components/ui/SuccessScreen/SuccessScreen';
import { RelationshipStatus } from '@/models/common/RelationshipStatus';
import {
  ManagementChildDtoIn,
  BulkChildStatusUpdateDto,
  BulkStatusUpdateResult,
  BulkStatusUpdateError,
} from '@/models/in/ManagementChildDtoIn';
import { postAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { getStatusColorSets } from '@/helpers/cssHelpers';
import styles from './BulkStatusChangeDialog.module.css';

interface BulkStatusChangeDialogProps {
  open: boolean;
  onClose: () => void;
  selectedChildren: ManagementChildDtoIn[];
  newStatus: RelationshipStatus;
  onSuccess: () => void;
}

const BulkStatusChangeDialog = ({
  open,
  onClose,
  selectedChildren,
  newStatus,
  onSuccess,
}: BulkStatusChangeDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<BulkStatusUpdateError | null>(null);
  const [dialogState, setDialogState] = useState<'confirmation' | 'success'>('confirmation');
  const [comment, setComment] = useState('');
  const [showInPersonalArea, setShowInPersonalArea] = useState(true);
  const [sendEmail, setSendEmail] = useState(false);

  // Memoize status color sets from CSS variables
  const statusColorSets = useMemo(() => getStatusColorSets(), []);

  // Validation: Comment is required when rejecting
  const isCommentRequired = newStatus === RelationshipStatus.Rejected;
  const isCommentMissing = isCommentRequired && !comment.trim();

  const handleSubmit = async () => {
    if (!currentUser?.token) return;

    setIsSubmitting(true);
    setError(null);

    const headers = { Authorization: `Bearer ${currentUser.token}` };
    const body: BulkChildStatusUpdateDto = {
      items: selectedChildren.map((child) => ({
        parentId: child.parentId,
        childId: child.childId,
      })),
      newStatus,
      comment: comment.trim() || undefined,
      showInPersonalArea,
      sendEmail,
    };

    try {
      await postAuth<BulkStatusUpdateResult, BulkChildStatusUpdateDto>(
        'management/children/bulk-status',
        headers,
        body
      );
      setDialogState('success');
    } catch (err: unknown) {
      console.error('Error in bulk status update:', err);

      // Try to extract error details from response
      const axiosError = err as { response?: { data?: BulkStatusUpdateError } };
      if (axiosError.response?.data) {
        setError(axiosError.response.data);
      } else {
        setError({
          message: t('childrenManagement.bulkUpdateFailed'),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setDialogState('confirmation');
    setComment('');
    setShowInPersonalArea(true);
    setSendEmail(false);
    onClose();
  };

  const handleSuccessClose = () => {
    handleClose();
    onSuccess();
  };

  const getStatusBadgeStyle = (status: RelationshipStatus) => ({
    backgroundColor: statusColorSets[status].backgroundAlpha,
    color: statusColorSets[status].border,
  });

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => !isOpen && handleClose()}
      title={t('childrenManagement.bulkStatusChange')}
    >
      {dialogState === 'confirmation' && (
        <div className={styles.content}>
          <p className={`body-md-regular ${styles.summaryMessage}`}>
            {t('childrenManagement.bulkStatusChangeConfirmation', {
              count: selectedChildren.length,
              status: t(`childrenManagement.status.${newStatus}`),
            })}
          </p>

          {/* Summary table */}
          <table className={styles.summaryTable}>
            <thead>
              <tr>
                <th>{t('israeliId')}</th>
                <th>{t('childrenManagement.childName')}</th>
                <th>{t('childrenManagement.parentName')}</th>
                <th>{t('childrenManagement.currentStatus')}</th>
                <th>{t('childrenManagement.newStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {selectedChildren.map((child) => (
                <tr key={`${child.parentId}-${child.childId}`}>
                  <td>{child.child.israeliId}</td>
                  <td>{child.child.firstName} {child.child.lastName}</td>
                  <td>{child.parent.firstName} {child.parent.lastName}</td>
                  <td>
                    <span
                      className={styles.statusBadge}
                      style={getStatusBadgeStyle(child.status)}
                    >
                      {t(`childrenManagement.status.${child.status}`)}
                    </span>
                  </td>
                  <td>
                    <span
                      className={styles.statusBadge}
                      style={getStatusBadgeStyle(newStatus)}
                    >
                      {t(`childrenManagement.status.${newStatus}`)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Visibility & Email options */}
          <div className={styles.emailOption}>
            <ToggleSwitch
              label={t('officeComments.showInPersonalArea')}
              checked={showInPersonalArea}
              onChange={setShowInPersonalArea}
            />
            <ToggleSwitch
              label={t('sendEmailNotification')}
              checked={sendEmail}
              onChange={setSendEmail}
            />
          </div>

          {/* Comment section */}
          <div className={styles.commentSection}>
            <label htmlFor="bulk-comment" className={`body-md-semibold ${styles.label}`}>
              {t('childrenManagement.comment')}
              {isCommentRequired && <span className={styles.required}> *</span>}
            </label>
            <textarea
              id="bulk-comment"
              className={styles.textarea}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('childrenManagement.commentPlaceholder')}
              rows={3}
            />
            {isCommentMissing && (
              <p className={styles.validationError}>
                {t('childrenManagement.noteRequired')}
              </p>
            )}
            {sendEmail && (
              <p className="body-xs-sm-regular text-(--font-body-2)">
                {t('noteSentByEmail')}
              </p>
            )}
          </div>

          {/* Error display with child details */}
          {error && (
            <div className={styles.errorSection}>
              <p className={styles.errorMessage}>{error.message}</p>
              {error.failedChildName && (
                <p className={styles.errorDetails}>
                  {t('childrenManagement.failedChild')}: {error.failedChildName}
                  {error.failedParentName && ` (${t('childrenManagement.parent')}: ${error.failedParentName})`}
                </p>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className={styles.actions}>
            <button
              className={styles.secondaryButton}
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t('cancel')}
            </button>
            <button
              className={styles.primaryButton}
              onClick={handleSubmit}
              disabled={isSubmitting || isCommentMissing}
            >
              {isSubmitting ? t('loading') : t('confirm')}
            </button>
          </div>
        </div>
      )}

      {dialogState === 'success' && (
        <SuccessScreen
          title={t('childrenManagement.bulkUpdateSuccess')}
          description={t('childrenManagement.bulkUpdateSuccessDescription', {
            count: selectedChildren.length,
          })}
          primaryButtonText={t('close')}
          primaryButtonAction={handleSuccessClose}
        />
      )}
    </MainDialogLayout>
  );
};

export default BulkStatusChangeDialog;

